import { useTranslation } from 'next-i18next'
import { clsx } from 'clsx'
import Change from '@components/shared/Change'
import { useRecentTrades } from 'hooks/useRecentTrades'
import {
  TokenPairSummary,
  useTokenPairSummary,
} from 'hooks/useTokenPairSummary'
import { useMemo, useState } from 'react'
import { formatCurrencyValue, formatNumericValue } from 'utils/numbers'
import { milliseconds } from 'date-fns'

type Changes = Record<keyof TokenPairSummary, number | null>

const INTERVAL_NAMES = {
  m5: '5m',
  h1: '1h',
  h6: '6h',
  d1: '1d',
} as const satisfies Record<keyof TokenPairSummary, string>

const CHANGE_INTERVALS = Object.keys(
  INTERVAL_NAMES,
) as (keyof typeof INTERVAL_NAMES)[]

const TokenChangeStats = () => {
  const { t } = useTranslation('token-page')
  const { sellVolume, buyVolume, buys, sells } = useRecentTrades()
  const {
    tokenStats,
    change5mPercentage,
    change1hPercentage,
    change6hPercentage,
    change24hPercentage,
  } = useTokenPairSummary({ refetchInterval: milliseconds({ seconds: 5 }) })
  const changePercentages: Changes = {
    m5: change5mPercentage,
    h1: change1hPercentage,
    h6: change6hPercentage,
    d1: change24hPercentage,
  }
  const [activeInterval, setActiveInterval] =
    useState<keyof TokenPairSummary>('d1')

  const activeIntervalStats = tokenStats?.[activeInterval]

  const trades = useMemo(() => {
    const initialBuys = activeIntervalStats?.trades?.buy?.trades || 0
    const initialSells = activeIntervalStats?.trades?.sell?.trades || 0
    return initialBuys + initialSells + buys + sells
  }, [activeIntervalStats, buys, sells])

  // Both tokenStats volumes (Birdeye token_overview) and realtime trade
  // volumes (Birdeye trade.price * size) are USD-denominated now.
  const [initialBuyVolume, initialSellVolume] = useMemo(() => {
    return [
      activeIntervalStats?.volume?.buy?.volume || 0,
      activeIntervalStats?.volume?.sell?.volume || 0,
    ]
  }, [activeIntervalStats])

  const volume = useMemo(() => {
    return initialBuyVolume + initialSellVolume + buyVolume + sellVolume
  }, [initialBuyVolume, initialSellVolume, buyVolume, sellVolume])

  return (
    <div className="shared-container h-full">
      <div className="grid grid-cols-4">
        {CHANGE_INTERVALS.map((interval, i) => (
          <button
            className={clsx(
              'col-span-2 flex h-12 w-full flex-col items-center justify-center border-b border-th-bkg-4 md:col-span-1 md:first:rounded-tl-xl md:last:rounded-tr-xl',
              activeInterval === interval
                ? 'bg-th-bkg-3 text-th-fgd-1'
                : 'text-th-fgd-4 md:hover:bg-th-bkg-3',
              { 'rounded-tr-xl md:rounded-tr-none': i === 1 },
            )}
            onClick={() => setActiveInterval(interval)}
            key={interval}
          >
            <span>
              {t(`change-stats.intervals.${INTERVAL_NAMES[interval]}`)}
            </span>
            {changePercentages[interval] != null &&
            Number.isFinite(changePercentages[interval]) ? (
              <Change
                change={changePercentages[interval]}
                suffix="%"
                size="small"
              />
            ) : (
              '–'
            )}
          </button>
        ))}
      </div>
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <div className="col-span-12 md:col-span-6">
            <p className="whitespace-nowrap">
              {t('change-stats.trades', {
                interval: t(
                  `change-stats.intervals.${INTERVAL_NAMES[activeInterval]}`,
                ),
              })}
            </p>
            <span className="font-mono text-th-fgd-1">
              {trades ? formatNumericValue(trades) : '–'}
            </span>
          </div>
          <div className="col-span-12 md:col-span-6">
            <p className="whitespace-nowrap">
              {t('change-stats.volume', {
                interval: t(
                  `change-stats.intervals.${INTERVAL_NAMES[activeInterval]}`,
                ),
              })}
            </p>
            <span className="font-mono text-th-fgd-1">
              {volume ? formatCurrencyValue(volume) : '–'}
            </span>
          </div>
          <div className="col-span-12 space-y-3 border-t border-th-bkg-4 pt-4">
            <div className="grid grid-cols-2 gap-4 lg:gap-6">
              <div className="col-span-2 md:col-span-1">
                <BuyVsSell
                  buyTitle={t('change-stats.buy')}
                  buyValue={
                    activeIntervalStats?.trades?.buy?.trades
                      ? activeIntervalStats.trades.buy.trades + buys
                      : 0
                  }
                  sellTitle={t('change-stats.sell')}
                  sellValue={
                    activeIntervalStats?.trades?.sell?.trades
                      ? activeIntervalStats.trades.sell.trades + sells
                      : 0
                  }
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <BuyVsSell
                  buyTitle={t('change-stats.buy-vol')}
                  buyValue={initialBuyVolume + buyVolume}
                  sellTitle={t('change-stats.sell-vol')}
                  sellValue={initialSellVolume + sellVolume}
                  isUsd
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TokenChangeStats

const BuyVsSell = ({
  buyTitle,
  buyValue,
  sellTitle,
  sellValue,
  isUsd,
}: {
  buyTitle: string
  buyValue: number | undefined
  sellTitle: string
  sellValue: number | undefined
  isUsd?: boolean
}) => {
  return (
    <>
      <div className="flex w-full justify-between">
        <div>
          <p>{buyTitle}</p>
          <span className="font-mono text-th-fgd-1">
            {buyValue
              ? isUsd
                ? formatCurrencyValue(buyValue)
                : formatNumericValue(buyValue)
              : '–'}
          </span>
        </div>
        <div>
          <p className="text-right">{sellTitle}</p>
          <span className="text-right font-mono text-th-fgd-1">
            {sellValue
              ? isUsd
                ? formatCurrencyValue(sellValue)
                : formatNumericValue(sellValue)
              : '–'}
          </span>
        </div>
      </div>
      {buyValue && sellValue ? (
        <div className="mt-1 flex w-full space-x-0.5">
          <div
            className="h-1 rounded-l bg-th-up"
            style={{
              width: `${(buyValue / (buyValue + sellValue)) * 100}%`,
            }}
          />
          <div
            className="h-1 rounded-r bg-th-down"
            style={{
              width: `${(sellValue / (buyValue + sellValue)) * 100}%`,
            }}
          />
        </div>
      ) : null}
    </>
  )
}
